import { useQuery } from '@apollo/client';
import { ContentBlock, Loader } from 'braid-design-system';
import { useSetAtom } from 'jotai';
import type { ReactNode } from 'react';

import type { Country } from '../entities/Country';
import { PAYMENT_TOGGLE } from '../graphql';
import {
  maintenanceInfoAtom,
  paymentMethodInMaintenanceAtom,
} from '../usecases/Maintenance/MaintenanceStore';
import { getMaintenance } from '../usecases/Maintenance/getMaintenance';
import { getPaymentMethodsInMaintenance } from '../usecases/Maintenance/getPaymentMethodsInMaintenance';
import { site } from '../utils/constants';

interface PaymentToggleData {
  getPaymentMethodForMaintenance: {
    maintenance: Maintenance;
    paymentMethods: Maintenance[];
  };
}

export interface PaymentToggleVariables {
  site: string;
  country: string;
}

interface PaymentMediumType {
  value: string;
  disablePayment: boolean;
}

export interface DisableMessageType {
  en: string;
  id?: string;
  th?: string;
}

export interface Maintenance {
  paymentMethod: string;
  disablePayment?: boolean;
  showMaintenance?: boolean;
  disableMessage: DisableMessageType;
  paymentMedium?: PaymentMediumType[];
}

export interface MaintenanceVariable {
  maintenance: Maintenance;
  paymentMethods: Maintenance[];
}

export const MaintenanceProvider = (props: {
  children: ReactNode;
  country: Country;
}) => {
  const setMaintenanceInfoAtom = useSetAtom(maintenanceInfoAtom);
  const setPaymentMethodInMaintenanceAtom = useSetAtom(
    paymentMethodInMaintenanceAtom,
  );
  const { children, country } = props;

  const { loading } = useQuery<PaymentToggleData, PaymentToggleVariables>(
    PAYMENT_TOGGLE,
    {
      variables: { site, country },
      onCompleted: (data) => {
        const m = data.getPaymentMethodForMaintenance;
        setMaintenanceInfoAtom(getMaintenance(m || undefined));
        setPaymentMethodInMaintenanceAtom(
          getPaymentMethodsInMaintenance(m || undefined),
        );
      },
      onError: () => {
        setMaintenanceInfoAtom(undefined);
        setPaymentMethodInMaintenanceAtom(undefined);
      },
    },
  );

  if (loading) {
    return (
      <ContentBlock width="large">
        <Loader />
      </ContentBlock>
    );
  }

  return <>{children}</>;
};
